/**
 * C03. 怪獸收集 + 進化 — 學習端 only (Prodigy / Pokémon style meta-game).
 *
 * Spec: docs/GAME_TECHNIQUES_WIKI.md §C03 (Pokémon Candy / GO Evolution).
 * Pillar: 長期 Retention (Pokémon 機制顯示每日登入率 +30%).
 *
 * Responsibility split:
 *   - `monsters-data.json`     — catalogue (10 lines × 3 tier = 30 defs).
 *                                Append-only after ship.
 *   - `monsters.ts` (this)     — pure state machine (catch / evolve / dex
 *                                snapshot). NO DOM imports.
 *   - `monsters-ui.ts`         — home reveal banner + dex panel + evolve
 *                                celebration.
 *   - `monsters-hooks.ts`      — listen ef:* events (streak / badge /
 *                                lesson-complete / boss-defeated) → roll
 *                                catch chance.
 *
 * 🔥 學習與遊戲分離 (CLAUDE.md 紅線):
 *   This module touches PlayerProgress.monsters only. It does NOT import
 *   anything from src/game/. Monster sprites are emoji placeholders so the
 *   feature ships without art deps; boss-designer can swap to PNG later
 *   via the same `sprite` field.
 *
 *   Catch / evolve does NOT touch faction-XP / freeXP — monsters are a
 *   pure collection meta-game. The reveal banner + dex are 學習端-only
 *   surfaces; the battle scene never sees monsters.
 *
 * Catch model (9-year-old appropriate, NOT gambling):
 *   - 每隻 monster def 有 `rarity` (common / uncommon / legendary).
 *   - 不同 event 給不同 base catch chance (`rollCatch` is pure helper,
 *     consumer passes its own rng + event-tag bias). Default biases
 *     designed so a normal lesson session has ~1 catch / 5 lessons,
 *     streak milestone almost guaranteed, boss-defeated good chance.
 *   - 進化 cost = 3 (Pokémon Candy-style triadic fusion).
 *
 * Why a separate file (and not folded into badges / season-pass):
 *   Different state shape (per-id COUNT, not boolean), different reveal
 *   semantics (banner shows the JUST-caught monster, not just a flag),
 *   different evolve mechanic (tier-up consumes duplicates). Folding
 *   would muddy badges.ts which is pure-boolean unlock.
 */

import type { FactionId, PlayerProgress } from '../../shared/types';
import { saveProgress } from '../../shared/progress';
import catalogueData from './monsters-data.json';

// ── Catalogue ───────────────────────────────────────────────────────────────

export type MonsterTier = 1 | 2 | 3;
export type MonsterRarity = 'common' | 'uncommon' | 'legendary';

export interface MonsterDef {
  /** Stable id — persisted in PlayerProgress.monsters.caught. NEVER rename after ship. */
  id: string;
  faction: FactionId;
  tier: MonsterTier;
  name: string;
  /** Emoji placeholder. boss-designer may swap to PNG path later. */
  sprite: string;
  /** id of the tier-N-1 monster this fuses from (null on base/tier1). */
  evolveFrom: string | null;
  /** id of the tier-N+1 monster this fuses into (null on capstone/tier3). */
  evolveTo: string | null;
  description: string;
  rarity: MonsterRarity;
}

interface CatalogueJson {
  _doc?: string;
  evolveCost: number;
  monsters: MonsterDef[];
}

const RAW = catalogueData as unknown as CatalogueJson;

/** Default duplicate count needed to evolve one tier (Pokémon Candy-style). */
export const EVOLVE_COST: number = RAW.evolveCost;

/** Frozen catalogue — never mutate at runtime. */
export const MONSTER_CATALOGUE: readonly MonsterDef[] = Object.freeze(RAW.monsters.slice());

const ID_INDEX: ReadonlyMap<string, MonsterDef> = new Map(
  MONSTER_CATALOGUE.map((m) => [m.id, m] as const),
);

/** Lookup a monster def by id (null if not in catalogue). */
export function findMonsterDef(id: string): MonsterDef | null {
  return ID_INDEX.get(id) ?? null;
}

/** All monster defs that belong to the given faction. */
export function monstersByFaction(faction: FactionId): MonsterDef[] {
  return MONSTER_CATALOGUE.filter((m) => m.faction === faction);
}

/** All monster defs at the given tier. */
export function monstersByTier(tier: MonsterTier): MonsterDef[] {
  return MONSTER_CATALOGUE.filter((m) => m.tier === tier);
}

// ── State helpers ───────────────────────────────────────────────────────────

/**
 * Lazy-init `p.monsters`. Mutates p; returns the non-optional shape so
 * callers don't need to re-narrow. Mirrors the badges.ts / season-pass.ts
 * getter pattern.
 */
function getMonsters(p: PlayerProgress): NonNullable<PlayerProgress['monsters']> {
  if (!p.monsters) {
    p.monsters = { caught: {} };
  }
  if (!p.monsters.caught || typeof p.monsters.caught !== 'object') {
    p.monsters.caught = {};
  }
  return p.monsters;
}

/** Count of a specific monster id currently in the kid's collection (0 if none). */
export function caughtCount(p: PlayerProgress, monsterId: string): number {
  const c = p.monsters?.caught?.[monsterId];
  return typeof c === 'number' && c > 0 ? c : 0;
}

/** True iff the kid has caught at least 1 of this monster id. */
export function hasCaught(p: PlayerProgress, monsterId: string): boolean {
  return caughtCount(p, monsterId) > 0;
}

/** Total distinct monsters caught (excludes catalogue orphans). */
export function distinctCaughtCount(p: PlayerProgress): number {
  const caught = p.monsters?.caught ?? {};
  let n = 0;
  for (const id of Object.keys(caught)) {
    if (ID_INDEX.has(id) && (caught[id] ?? 0) > 0) n++;
  }
  return n;
}

// ── Catch ───────────────────────────────────────────────────────────────────

/** Window event fired when a monster is caught. mascot / banner listen. */
export interface MonsterCaughtEventDetail {
  monsterId: string;
  monster: MonsterDef;
  /** True iff this id was caught for the FIRST time (new dex entry). */
  isNew: boolean;
  at: number;
}

/**
 * Source-tag for `rollCatch`. Keeps the audit trail grep'able so we know
 * every catch path is explicit (no silent grants).
 */
export type CatchSource =
  | 'lesson-complete'
  | 'boss-defeated'
  | 'streak-milestone'
  | 'badge-unlocked'
  | 'manual-test';

/**
 * Per-source base catch chance (0..1). Tuned for a 9-year-old's session
 * (≈1 catch per 5 lessons on average, streak milestone almost guaranteed
 * since it only fires every 7/30/100 days, boss defeat solid reward).
 *
 * NOT gambling — these are gentle deterministic biases, not loot boxes.
 * Source of truth lives here in code (not JSON) so balance changes show
 * up in git blame. Tunable per CLAUDE.md 「kid 適齡」 紅線.
 */
export const CATCH_CHANCE_BY_SOURCE: Record<CatchSource, number> = {
  'lesson-complete': 0.2,    // ~1-in-5 lessons
  'boss-defeated':   0.6,    // beat a boss = strong reward
  'streak-milestone': 1.0,   // 7/30/100 day milestone = guaranteed
  'badge-unlocked':  0.5,    // earning a badge = solid reward
  'manual-test':     1.0,    // tests / debug — always catch
};

/**
 * Rarity weight when picking WHICH monster to catch on a successful roll.
 * Higher = more likely to roll. Legendaries are rare to keep them special.
 */
export const RARITY_WEIGHT: Record<MonsterRarity, number> = {
  common:    50,
  uncommon:  18,
  legendary: 2,
};

/**
 * Decide whether a catch attempt SUCCEEDS for the given source.
 * Pure — no mutation. Caller passes its own rng for determinism.
 *
 * Returns true iff the roll fires AND we have at least one tier-1 monster
 * to award (we never auto-grant tier-2+ to keep evolution meaningful).
 */
export function rollCatch(source: CatchSource, rng: () => number = Math.random): boolean {
  const chance = CATCH_CHANCE_BY_SOURCE[source] ?? 0;
  if (chance <= 0) return false;
  const r = rng();
  return r < chance;
}

/**
 * Pick which tier-1 monster to award on a successful catch. Weighted by
 * rarity (legendaries rare, commons frequent). Optionally filter by
 * `preferFaction` — if the source has a faction context (e.g. lesson
 * subject), we bias to that faction's pool but FALL BACK to global if
 * the faction has nothing to award.
 *
 * Returns null only if catalogue has zero tier-1 monsters (impossible in
 * the shipped catalogue — defensive guard for hand-crafted tests).
 */
export function pickCatchTarget(
  preferFaction: FactionId | null = null,
  rng: () => number = Math.random,
): MonsterDef | null {
  const tier1 = monstersByTier(1);
  if (tier1.length === 0) return null;
  // Build candidate pool — preferred faction first, fall back to global.
  let pool = preferFaction === null ? tier1 : tier1.filter((m) => m.faction === preferFaction);
  if (pool.length === 0) pool = tier1;

  // Weighted pick by rarity.
  const totalWeight = pool.reduce((sum, m) => sum + (RARITY_WEIGHT[m.rarity] ?? 1), 0);
  if (totalWeight <= 0) return pool[Math.floor(rng() * pool.length)] ?? null;
  let r = rng() * totalWeight;
  for (const m of pool) {
    r -= RARITY_WEIGHT[m.rarity] ?? 1;
    if (r <= 0) return m;
  }
  return pool[pool.length - 1] ?? null;
}

/**
 * Catch a monster by id — appends 1 to the per-id counter. Idempotent in
 * the sense of "always grants exactly 1", NOT in "no double-catch": a
 * second call increments the count to 2 (that's the WHOLE point — duplicates
 * fuse into evolutions).
 *
 * Persists via `saveProgress`. Dispatches `ef:monster-caught` on success
 * (best-effort — swallowed in non-DOM envs).
 *
 * Returns the def + isNew flag on success, null when id not in catalogue.
 */
export function catchMonster(
  p: PlayerProgress,
  monsterId: string,
  nowMs: number = Date.now(),
): { def: MonsterDef; isNew: boolean } | null {
  const def = findMonsterDef(monsterId);
  if (!def) return null;
  const monsters = getMonsters(p);
  const prev = monsters.caught[monsterId] ?? 0;
  const isNew = prev === 0;
  monsters.caught[monsterId] = prev + 1;
  monsters.lastNew = { id: monsterId, at: nowMs };

  try {
    saveProgress(p);
  } catch {
    // Test env may not stub localStorage — state already mutated.
  }
  if (typeof window !== 'undefined') {
    try {
      const detail: MonsterCaughtEventDetail = { monsterId, monster: def, isNew, at: nowMs };
      window.dispatchEvent(new CustomEvent('ef:monster-caught', { detail }));
    } catch {
      // jsdom without CustomEvent shim — ignore.
    }
  }
  return { def, isNew };
}

// ── Evolve ──────────────────────────────────────────────────────────────────

/**
 * Window event fired when an evolution completes. Mascot / overlay listen.
 */
export interface MonsterEvolvedEventDetail {
  fromId: string;
  fromMonster: MonsterDef;
  toId: string;
  toMonster: MonsterDef;
  at: number;
}

/** True iff the kid currently has enough duplicates of `monsterId` to evolve. */
export function canEvolve(p: PlayerProgress, monsterId: string): boolean {
  const def = findMonsterDef(monsterId);
  if (!def || !def.evolveTo) return false;
  return caughtCount(p, monsterId) >= EVOLVE_COST;
}

/**
 * Evolve a monster — consume EVOLVE_COST duplicates of `fromId`, grant 1
 * of its `evolveTo` target. Idempotent guard: returns null if requirements
 * not met (no def / not enough dupes / already capstone tier 3).
 *
 * Side effects on success:
 *   1. Decrement caught[fromId] by EVOLVE_COST (or delete if it hits 0)
 *   2. Increment caught[toId] by 1 (creates entry if new)
 *   3. Set lastNew = { id: toId, at } (banner uses this for celebration)
 *   4. saveProgress(p)
 *   5. Dispatch `ef:monster-evolved` (best-effort)
 *
 * Returns the from + to defs on success, null on fail.
 */
export function evolveMonster(
  p: PlayerProgress,
  fromId: string,
  nowMs: number = Date.now(),
): { from: MonsterDef; to: MonsterDef } | null {
  const fromDef = findMonsterDef(fromId);
  if (!fromDef || !fromDef.evolveTo) return null;
  const toDef = findMonsterDef(fromDef.evolveTo);
  if (!toDef) return null;
  const monsters = getMonsters(p);
  const have = monsters.caught[fromId] ?? 0;
  if (have < EVOLVE_COST) return null;

  // Consume duplicates
  const remaining = have - EVOLVE_COST;
  if (remaining > 0) monsters.caught[fromId] = remaining;
  else delete monsters.caught[fromId];

  // Grant evolved form
  monsters.caught[toDef.id] = (monsters.caught[toDef.id] ?? 0) + 1;
  monsters.lastNew = { id: toDef.id, at: nowMs };

  try {
    saveProgress(p);
  } catch {
    // see catchMonster
  }
  if (typeof window !== 'undefined') {
    try {
      const detail: MonsterEvolvedEventDetail = {
        fromId,
        fromMonster: fromDef,
        toId: toDef.id,
        toMonster: toDef,
        at: nowMs,
      };
      window.dispatchEvent(new CustomEvent('ef:monster-evolved', { detail }));
    } catch {
      // jsdom — ignore
    }
  }
  return { from: fromDef, to: toDef };
}

/**
 * Auto-evolve any monsters whose dupe count >= EVOLVE_COST. Returns the
 * list of evolutions performed in order. Idempotent — re-running returns
 * [] when nothing qualifies. Used by the home tick + post-catch sweep so
 * kids don't have to manually tap "evolve" (Prodigy auto-evolves on cap).
 *
 * Caps at one evolution per source id per call to avoid cascading
 * tier1→tier2→tier3 chains in a single click (one celebration per tap).
 */
export function autoEvolveAll(
  p: PlayerProgress,
  nowMs: number = Date.now(),
): Array<{ from: MonsterDef; to: MonsterDef }> {
  const monsters = getMonsters(p);
  const performed: Array<{ from: MonsterDef; to: MonsterDef }> = [];
  // Snapshot keys so deletions mid-loop don't skip ids.
  const ids = Object.keys(monsters.caught);
  for (const id of ids) {
    if (!canEvolve(p, id)) continue;
    const result = evolveMonster(p, id, nowMs);
    if (result) performed.push(result);
  }
  return performed;
}

// ── Read `lastNew` (one-shot reveal pattern) ────────────────────────────────

/**
 * Read + CLEAR `lastNew`. Home reveal banner calls this so the "✨ 抓到新
 * 怪獸!" reveal fires exactly once per catch. Persists via saveProgress.
 * Returns the def (or null when nothing pending).
 */
export function consumeLastNew(p: PlayerProgress): MonsterDef | null {
  const ln = p.monsters?.lastNew;
  if (!ln) return null;
  const def = findMonsterDef(ln.id);
  if (p.monsters) {
    delete p.monsters.lastNew;
    try {
      saveProgress(p);
    } catch {
      // ignore — see catchMonster
    }
  }
  return def;
}

// ── Dex snapshot for UI ─────────────────────────────────────────────────────

export interface MonsterDexEntry {
  def: MonsterDef;
  count: number;
  caught: boolean;
}

export interface MonsterDexSnapshot {
  /** All defs in catalogue order, with per-id count / caught flag. */
  entries: MonsterDexEntry[];
  /** Distinct caught (excluding catalogue orphans). */
  distinctCaught: number;
  /** Catalogue total. */
  total: number;
  /** Grouped by faction for the UI tabs. */
  byFaction: Record<FactionId, MonsterDexEntry[]>;
}

/**
 * Build a full dex snapshot — every catalogue entry with its caught state.
 * Read-only; never mutates `p`. Used by the dex panel UI to render the
 * Pokédex-style grid (caught entries full-color, locked entries grey).
 */
export function buildDexSnapshot(p: PlayerProgress): MonsterDexSnapshot {
  const caught = p.monsters?.caught ?? {};
  const entries: MonsterDexEntry[] = MONSTER_CATALOGUE.map((def) => {
    const count = caught[def.id] ?? 0;
    return { def, count, caught: count > 0 };
  });
  const byFaction: Record<FactionId, MonsterDexEntry[]> = {
    celestial: [],
    runic: [],
    techno: [],
    elemental: [],
    dominion: [],
  };
  for (const e of entries) byFaction[e.def.faction].push(e);
  return {
    entries,
    distinctCaught: distinctCaughtCount(p),
    total: MONSTER_CATALOGUE.length,
    byFaction,
  };
}
